<template>
    <div :class="{'display-inline-flex': fluid}">
        <div :class="{'stack-s': (stackTopBlock && !fluid)}">
            <v-popper-select
                label="Schedule type"
                :options="scheduleOptions"
                :validate="validate.method"
                :value="scheduleInstruction.method"
                @input="updateMethod"/>
        </div>
        <div :class="{'display-inline-flex': fluid}">
            <template v-if="scheduleInstruction.method === scheduleInstructionTypes.intelligent">
                <span class="inline-xs">
                    Within
                </span>
                <v-inline-input
                    class="inline-xs rule-action-schedule-payment-inline-inputs"
                    :validate="validate.duration"
                    v-model="scheduleInstruction.duration"/>
                <v-popper-select
                    :options="dateIntervalOptions"
                    :value="scheduleInstruction.unit"
                    :validate="validate.unit"
                    @input="value => updateInstruction('unit', value)"/>
            </template>
            <template v-if="scheduleInstruction.method === scheduleInstructionTypes.dateInterval">
                <div class="grid-fitted">
                    <div class="grid-item no-flex">
                        <v-inline-input
                            class="inline-s rule-action-schedule-payment-inline-inputs"
                            :validate="validate.duration"
                            v-model="scheduleInstruction.duration"/>
                    </div>
                    <div class="grid-item">
                        <v-popper-select
                            class="inline-xs"
                            :options="dateIntervalOptions"
                            :value="scheduleInstruction.unit"
                            @input="value => updateInstruction('unit', value)"/>
                        <span v-if="!commonSchedule">later</span>
                    </div>
                </div>
            </template>
            <template v-if="scheduleInstruction.method === scheduleInstructionTypes.dayOfMonth">
                <div :class="{'stack-s': !fluid}">
                    <span class="inline-s">On the</span>
                    <v-popper-select
                        label="Day"
                        :options="dayOptions"
                        :value="scheduleInstruction.day"
                        :validate="validate.day"
                        @input="value => updateInstruction('day', value)"/>
                    <span>of the month</span>
                </div>
                <div class="display-flex">
                    <span class="inline-s text-muted">
                        <span v-if="fluid">&nbsp;</span>(Optional Time)
                    </span>
                    <v-date-input :no-flex="true"
                                  type="time"
                                  :value="scheduleInstruction.time"
                                  :validate="validate.time"
                                  @input="updateTime">
                        <template slot="hours-input" slot-scope="hours">
                            <v-popper-select
                                label="Hours"
                                :options="prepareDateTimeOptions(hours.options)"
                                :value="hours.value"
                                @input="hours.input"/>
                        </template>
                        <template slot="minutes-input" slot-scope="minutes">
                            <v-popper-select
                                label="Minutes"
                                :options="prepareDateTimeOptions(minutes.options)"
                                :value="minutes.value"
                                @input="minutes.input"/>
                        </template>
                    </v-date-input>
                </div>
            </template>
            <div v-if="scheduleInstruction.method === scheduleInstructionTypes.dayOfWeek"
                 :class="{'display-inline-flex': fluid}">
                <div :class="{'stack-s': !fluid}">
                    <v-popper-select
                        class="inline-xs"
                        label="Week"
                        :options="weekOptions"
                        :value="scheduleInstruction.week"
                        :validate="validate.week"
                        @input="value => updateInstruction('week', value)"/>
                    <v-popper-select
                        label="Day"
                        :options="weekDaysOptions"
                        :value="scheduleInstruction.day"
                        :validate="validate.day"
                        @input="value => updateInstruction('day', value)"/>
                </div>
                <div class="display-flex">
                    <span class="inline-s text-muted">
                        (Optional Time)
                    </span>
                    <v-date-input
                        class="inline-m"
                        type="time"
                        :value="scheduleInstruction.time"
                        :validate="validate.time"
                        @input="updateTime">
                        <template slot="hours-input" slot-scope="hours">
                            <v-popper-select
                                label="Hours"
                                :options="prepareDateTimeOptions(hours.options)"
                                :value="hours.value"
                                @input="value => inputTime('hours', value, hours.input)"/>
                        </template>
                        <template slot="minutes-input" slot-scope="minutes">
                            <v-popper-select
                                label="Minutes"
                                :options="prepareDateTimeOptions(minutes.options)"
                                :value="minutes.value"
                                @input="value => inputTime('minutes', value, minutes.input)"/>
                        </template>
                    </v-date-input>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import moment from 'moment';
    import _ from '@/common/helpers';
    import {DateTimeFormats} from '@/common/datetime-formats';
    import {
        vDateInput, vInlineInput, vPopperSelect, vSelect,
    } from '@/components/form';
    import ScheduleInstructionModel from '@/common/models/rules/schedule-instruction-model';

    const defaultFormatterTypeDropDownLabel = ({value}, commonMethods) => {
        let dateIntervalLabel = 'after a certain amount of time';
        if (commonMethods) {
            dateIntervalLabel = 'certain amount of time';
        }
        const map = {
            'date-interval': dateIntervalLabel,
            'day-of-month': 'on a specific date',
            'day-of-week': 'on a specific day',
            intelligent: 'intelligently',
            auto: 'automatically',
        };
        return `${map[value] || _.unKebabCase(value)}`;
    };

    export default {
        name: 'define-schedule-view',
        components: {
            vSelect,
            vPopperSelect,
            vInlineInput,
            vDateInput,
        },
        props: {
            validate: {
                type: Object,
            },
            scheduleInstruction: {
                type: Object,
                required: true,
            },
            formatTypeDropDownLabel: {
                type: Function,
                required: false,
                default: ({value, formatter}) => formatter({value}),
            },
            fluid: {
                type: Boolean,
                default: false,
            },
            invoiceRetrySchedule: {
                type: Boolean,
                default: false,
            },
            commonSchedule: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                scheduleOptions: Object.values(
                    this.getMethods(),
                ).map(value => ({
                    label: this.formatTypeDropDownLabel({
                        value,
                        formatter: scheduleType => defaultFormatterTypeDropDownLabel(scheduleType, this.commonSchedule),
                    }),
                    value,
                })),
                // todo check values list
                dateIntervalOptionsOrigin: Object.values(
                    ScheduleInstructionModel.Units,
                ).filter(v => v[v.length - 1] !== 's').map(value => ({
                    label: _.unKebabCase(value),
                    value,
                })),
                weekOptions: Object.values(
                    ScheduleInstructionModel.WeekTypes,
                ).map(value => ({
                    label: _.unKebabCase(value),
                    value,
                })),
                weekDaysOptions: moment.weekdays().map(label => ({
                    label,
                    value: label,
                })),
                dayOptions: new Array(31)
                    .fill(0)
                    .map((v, i) => {
                        const value = i + 1;
                        const label = this.$filters.ordinal(value);
                        return {
                            value,
                            label,
                        };
                    }),
                scheduleInstructionTypes: this.getMethods(),
            };
        },
        computed: {
            stackTopBlock() {
                return [this.scheduleInstructionTypes.intelligent,
                        this.scheduleInstructionTypes.dateInterval,
                        this.scheduleInstructionTypes.dayOfMonth,
                        this.scheduleInstructionTypes.dayOfWeek,
                ].includes(this.scheduleInstruction.method);
            },
            dateIntervalOptions() {
                const {duration} = this.scheduleInstruction;
                return !duration
                    ? this.dateIntervalOptionsOrigin
                    : this.dateIntervalOptionsOrigin.map(item => ({
                        ...item,
                        label: _.pluralize(item.label, +duration),
                    }));
            },
        },
        methods: {
            getMethods() {
                if (this.invoiceRetrySchedule) {
                    return ScheduleInstructionModel.InvoiceRetryMethods;
                }
                if (this.commonSchedule) {
                    return ScheduleInstructionModel.CommonEventMethods;
                }
                return ScheduleInstructionModel.Methods;
            },
            prepareDateTimeOptions(options) {
                return [
                    {value: '', label: 'None'},
                    ...options.map(value => ({value, label: value})),
                ];
            },
            inputTime(name, value, handler) {
                if (!value) {
                    this.$emit('update', {time: null});
                } else {
                    handler(value);
                }
            },
            updateMethod(method) {
                const keys = Object.keys(this.scheduleInstruction);

                const defaultData = {
                    [this.scheduleInstructionTypes.dayOfMonth]: () => ({
                        day: this.dayOptions[0].value,
                        time: this.$tz().now().startOf('day'),
                    }),
                    [this.scheduleInstructionTypes.dayOfWeek]: () => ({
                        day: this.weekDaysOptions[0].value,
                        time: this.$tz().now().startOf('day'),
                    }),
                    [this.scheduleInstructionTypes.dateInterval]: () => ({
                        unit: this.dateIntervalOptions[0].value,
                    }),
                    [this.scheduleInstructionTypes.intelligent]: () => ({
                        unit: this.dateIntervalOptions[0].value,
                    }),
                };
                // reset values
                const data = {
                    ...keys.reduce((memo, key) => ({...memo, [key]: null}), {}),
                    ...{
                    method,
                    time: null,
                },
                };
                // set default values
                const result = defaultData[method] ? {...data, ...defaultData[method]()} : data;

                this.$emit('update', result);
            },
            updateTime(value) {
                const time = value ? this.$filters.dateLocal(value, DateTimeFormats.time) : null;
                this.$emit('update', {time});
            },
            updateInstruction(name, value) {
                this.$emit('update', {[name]: value});
            },
        },
    };
</script>
